
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarNav } from "@/components/ui/sidebar/sidebar-nav";
import { toast } from "sonner";
import { useBusinessTypeFeatures } from "@/hooks/useBusinessTypeFeatures";
import { SidebarMenuProps, OwnerLink } from "./types";
import { SidebarLink } from "./SidebarLink";
import { SidebarEyeButton } from "./SidebarEyeButton";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { AccessCodeVerificationDialog } from "./AccessCodeVerificationDialog";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const SidebarMenu = ({
  links,
  userType,
  isInventoryActive,
  visibleMenuItems,
  authorizedFeatures,
  onEyeClick,
  shouldShowEyeIcon,
  isFeatureVisible,
  accessCodes,
}: SidebarMenuProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFeatureAccess } = useBusinessTypeFeatures();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasAccessCodes, setHasAccessCodes] = useState(false);
  const [selectedLink, setSelectedLink] = useState<OwnerLink | null>(null);
  const [isVerificationDialogOpen, setIsVerificationDialogOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const hasAnyCodes = Object.values(accessCodes).some(code => code && code.length > 0);
    setHasAccessCodes(hasAnyCodes);
  }, [accessCodes]);

  const isLinkActive = (href: string) => {
    if (href === '/owner' && location.pathname.startsWith('/owner/')) {
      return location.pathname === '/owner';
    }
    return location.pathname === href;
  };

  const handleLinkClick = async (e: React.MouseEvent, href: string, feature?: string, isDropdownItem?: boolean, item?: OwnerLink) => {
    e.preventDefault();

    if (href === "#") {
      setIsDropdownOpen(!isDropdownOpen);
      return;
    }

    if (isInventoryActive && href !== "/owner/stock") {
      toast.error("Veuillez terminer l'inventaire en cours avant de naviguer");
      return;
    }

    if (userType === "owner" && feature && feature !== "dashboard") {
      if (!hasFeatureAccess(feature)) {
        toast.error("Cette fonctionnalité n'est pas disponible pour votre type d'entreprise");
        return;
      }

      if (["settings", "budget", "staff"].includes(feature) && accessCodes[feature]) {
        setSelectedLink(item || null);
        setIsVerificationDialogOpen(true);
        return;
      }
    }

    navigate(href);
  };

  const handleVerificationSuccess = () => {
    setIsVerificationDialogOpen(false);
    if (selectedLink) {
      navigate(selectedLink.href);
    }
  };

  const guideSteps = [
    "1. Allez dans l'onglet Paramètres",
    "2. Cliquez sur l'onglet Sécurité",
    "3. Dans la section Codes d'accès, activez l'interrupteur pour l'onglet souhaité",
    "4. Cliquez sur Définir pour créer votre code",
    "5. Vous pouvez désactiver le code à tout moment avec l'interrupteur"
  ];

  const filteredLinks = links.filter((link: any) => {
    if ('hidden' in link && link.hidden) {
      return false;
    }
    return true;
  });

  return (
    <SidebarNav>
      <div className="space-y-2">
        {filteredLinks.map((item) => {
          const ownerLink = item as OwnerLink;
          const canHaveAccessCode = shouldShowEyeIcon(ownerLink.feature);
          
          if (ownerLink.isDropdownItem && !isDropdownOpen) {
            return null;
          }

          return (
            <div 
              key={item.name} 
              className={cn(
                "flex items-center gap-2 px-2 py-1 rounded-lg transition-all",
                ownerLink.isDropdownItem && "ml-4",
                isLinkActive(item.href) && "bg-primary/10"
              )}
            >
              <SidebarLink
                item={item}
                isActive={isLinkActive(item.href)}
                onClick={(e) => handleLinkClick(e, item.href, ownerLink.feature, ownerLink.isDropdownItem, ownerLink)}
                isDropdownItem={ownerLink.isDropdownItem}
              />
              {userType === "owner" && canHaveAccessCode && (
                <SidebarEyeButton
                  name={item.name}
                  feature={ownerLink.feature}
                  isVisible={visibleMenuItems.includes(ownerLink.feature || '')}
                  onEyeClick={onEyeClick}
                />
              )}
            </div>
          );
        })}

        {isDropdownOpen && (
          <div className="ml-4 mt-4 space-y-3">
            <div className="flex items-center gap-2 px-2">
              <p className="text-sm text-muted-foreground">
                Un code d'accès est requis pour accéder aux onglets Personnel, Budget et Paramètres.
              </p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="p-1.5 hover:bg-primary/10 rounded-full transition-colors">
                      <HelpCircle className="h-5 w-5 text-primary hover:text-primary/80" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent align="start" className="max-w-xs p-4 bg-primary text-primary-foreground">
                    <div className="space-y-2">
                      <p className="font-medium">Comment configurer un code d'accès ?</p>
                      <ul className="text-sm space-y-1">
                        {guideSteps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {!hasAccessCodes && (
              <div className="mx-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Important : Pour sécuriser votre compte, veuillez configurer les codes d'accès pour ces onglets. 
                  Allez dans <span className="font-medium">Paramètres → Sécurité</span> pour définir vos codes d'accès.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <AccessCodeVerificationDialog
        open={isVerificationDialogOpen}
        onOpenChange={setIsVerificationDialogOpen}
        onVerify={handleVerificationSuccess}
        expectedCode={selectedLink?.feature ? accessCodes[selectedLink.feature] : undefined}
      />
    </SidebarNav>
  );
};
